import {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {SelectChangeEvent} from "@mui/material/Select";
import {useAuth} from "hooks/useAuth";
import Loading from "shared/ui/Loading";
import PageTitle from "shared/ui/PageTitle";
import GenericButton from "shared/ui/GenericButton";
import BoxContent from "shared/ui/BoxContent";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import FormField from "shared/form/FormField";
import CustomButton from "shared/ui/CustomButton";
import ErrorsList from "shared/ui/ErrorsList";
import {
    APIGetCustomSubcategory,
    APIPostCustomSubcategory,
    APIUpdateCustomSubcategory
} from "../api/customSubcategory";
import {
    APIPostCustomItemsGrades,
    APIPostCustomItemsSubjects,
} from "../api/customSubcategories";

export interface IFormCustomItemsEdit {
    subject: string;
    grade: string;
    subcategory: string;
}

const schema = yup
    .object({})
    .required();

const CustomSubcategory = () => {
    const {id = "", action} = useParams();
    const {update, user} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [item, setItem] = useState<any>();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [subjects, setSubjects] = useState<any[]>([]);
    const [subject, setSubject] = useState("");
    const [grades, setGrades] = useState<any[]>([]);
    const [grade, setGrade] = useState("");
    const [ready, setReady] = useState<boolean>(false);

    const methods = useForm<IFormCustomItemsEdit>({
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        const fetchSubjectsAndGrades = async () => {
            try {
                const subjectsResponse = await APIPostCustomItemsSubjects();
                if(!subjectsResponse.data.success) update("error", {show: true, code: "A002"});
                setSubjects(subjectsResponse.data.data.data.items);

                const gradesResponse = await APIPostCustomItemsGrades();
                if(!gradesResponse.data.success) update("error", {show: true, code: "A002"});
                setGrades(gradesResponse.data.data.data.items);

                setReady(true);
            } catch (e) {update("error", {show: true, code: "A001"})}
        };

        fetchSubjectsAndGrades();
    }, []);

    useEffect(() => {
        setLoading(true);
        if(!ready) return;
        if (id !== "") {
            APIGetCustomSubcategory({id: id})
                .then((response) => {
                    if (response.data.success) {
                        // console.log("getCustomItem OK", response);
                        if (response.data.data !== null) {
                            setItem(response.data.data.item);
                            setSubject(response.data.data.item.item_type_id);
                            setGrade(response.data.data.item.grade_id);
                        } else navigate("/pagina-nu-exista");
                    } else update("error", {show: true, code: "A002"});
                })
                .catch(() => update("error", {show: true, code: "A001"}))
                .finally(() => setLoading(false));
        } else {
            let locationArray = location.pathname.split("/");
            if (locationArray[locationArray.length - 1] === "adauga") {
                setItem({
                    type: {id: ""},
                    name: "",
                    item_type: "",
                    grade: {id: ""},
                });
                setEditMode(true);
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, ready]);

    useEffect(() => {
        if (id !== "") {
            methods.clearErrors();
            setErrorMessage("");
            setEditMode(action === "editare");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, id]);

    const onSubmit: SubmitHandler<IFormCustomItemsEdit> = (data) => {
        if (
            subject !== item.type.id ||
            grade !== item.grade.id ||
            data.subcategory !== item.name
        ) {
            methods.clearErrors();
            setErrorMessage("");
            update("loading", {show: true, modal: true});
            if (id !== "") {
                APIUpdateCustomSubcategory(id, {
                    subject: subject,
                    grade: grade,
                    name: data.subcategory,
                })
                    .then((response) => {
                        if (response.data.success) {
                            setItem(response.data.data.item);
                            setEditMode(false);
                            navigate(-1);
                        } else update("error", {show: true, code: "A002"});
                    })
                    .catch((err) => {
                        if (err.response.status === 400) {
                            let errors = err.response.data.data;
                            if (errors.subject) {
                                methods.setError("subject", {type: "server", message: errors.subject[0]});
                            }
                            if (errors.grade) {
                                methods.setError("grade", {type: "server", message: errors.grade[0]});
                            }
                            if (errors.name) {
                                methods.setError("subcategory", {type: "server", message: errors.name[0]});
                            }
                            update("loading", {show: false});
                        } else update("error", {show: true, code: "A001"});
                    })
                    .finally(() => update("loading", {show: false}));
            } else {
                APIPostCustomSubcategory({
                    subject: subject,
                    grade: grade,
                    name: data.subcategory
                })
                    .then((response) => {
                        if (response.data.success) {
                            setItem({
                                type: {id: subject},
                                name: data.subcategory,
                                item_type: subject,
                                grade: {id: grade},
                            });
                            setEditMode(false);
                            navigate(`/subcategorii-proprii/${response.data.data.item.id}`, {replace: true});
                            update("loading", {show: false});
                        } else update("error", {show: true, code: "A002"});
                    })
                    .catch((err) => {
                        if (err.response.status === 400) {
                            let errors = err.response.data.data;
                            if (errors.subject) {
                                methods.setError("subject", {type: "server", message: errors.subject[0]});
                            }
                            if (errors.grade) {
                                methods.setError("grade", {type: "server", message: errors.grade[0]});
                            }
                            if (errors.name) {
                                methods.setError("subcategory", {type: "server", message: errors.name[0]});
                            }
                            update("loading", {show: false});
                        } else update("error", {show: true, code: "A001"});
                    })
                    .finally(() => update("loading", {show: false}));
            }
        } else {
            setErrorMessage("Nu ați modificat niciun câmp.");
        }
    };

    const setSubjectHandler = (event: SelectChangeEvent) => {
        setGrade("");
        setSubject(event.target.value);
    }

    const setGradeHandler = (event: SelectChangeEvent) => {
        setGrade(event.target.value);
    }

    return (
        <Box component="main">
            {(loading) ? (
                <Loading show={true}/>
            ) : (
                <>
                    <PageTitle
                        back={true}
                        actions={
                            user.position === "teacher" && user.id === item.user_id ? (
                                editMode ? (
                                    <GenericButton
                                        type="close"
                                        onClick={() => {
                                            navigate(-1);
                                        }}
                                    />
                                ) : (
                                    <GenericButton
                                        type="edit"
                                        onClick={() => {
                                            navigate(`/subcategorii-proprii/${id}/editare`);
                                        }}
                                    />
                                )
                            ) : (
                                ""
                            )
                        }
                    >
                        {id !== "" ? (
                            <>
                                Subcategorie{" "}
                                <strong style={{fontWeight: 400}}>
                                    {item.id}
                                </strong>
                            </>
                        ) : (
                            <>Adaugă subcategorie</>
                        )}
                    </PageTitle>

                    <BoxContent>
                        <Box width="100%">
                            {!editMode ? (
                                <Grid container spacing={1.5} alignItems="center">
                                    <FormLabelBox title="Materie:"/>
                                    <FormFieldBox>{item.type.name}</FormFieldBox>
                                    <FormLabelBox title="Clasa:"/>
                                    <FormFieldBox>{item.grade.name}</FormFieldBox>
                                    <FormLabelBox title="Nume:"/>
                                    <FormFieldBox>{item.name}</FormFieldBox>
                                </Grid>
                            ) : (
                                <FormProvider {...methods}>
                                    <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                                        <Grid container spacing={1.5} alignItems="center">
                                            <FormLabelBox title="Materie:"/>
                                            <FormFieldBox>
                                                <FormField
                                                    name="subject"
                                                    type="controlled-select"
                                                    label="Alege materia"
                                                    value={subject}
                                                    options={subjects.map((item: { name: string; id: string }) => {
                                                        return {
                                                            label: item.name,
                                                            value: item.id,
                                                            ...item,
                                                        };
                                                    })}
                                                    errorMessage={methods.formState.errors.subject?.message}
                                                    error={!!methods.formState.errors.subject?.message}
                                                    onChange={setSubjectHandler}
                                                />
                                            </FormFieldBox>
                                            <FormLabelBox title="Clasă:"/>

                                            <FormFieldBox>
                                                <FormField
                                                    name="grade"
                                                    type="controlled-select"
                                                    label="Alege clasa"
                                                    value={grade}
                                                    options={grades.map((item: { name: string; id: string }) => {
                                                        return {
                                                            label: item.name,
                                                            value: item.id,
                                                            ...item,
                                                        };
                                                    })}
                                                    errorMessage={methods.formState.errors.grade?.message}
                                                    error={!!methods.formState.errors.grade?.message}
                                                    onChange={setGradeHandler}
                                                />
                                            </FormFieldBox>
                                            <FormLabelBox title="Subcategorie:"/>

                                            <FormFieldBox>
                                                <FormField
                                                    name="subcategory"
                                                    type="text"
                                                    label="Nume subcategorie"
                                                    defaultValue={item.name}
                                                    errorMessage={methods.formState.errors.subcategory?.message}
                                                    error={!!methods.formState.errors.subcategory?.message}
                                                />
                                            </FormFieldBox>
                                            <FormLabelBox/>
                                            <FormFieldBox>
                                                <CustomButton submit buttonType="textIcon" bgcolor="dark">
                                                    {id !== "" ? "SALVEAZĂ DATELE" : "ADAUGĂ SUBCATEGORIE"}
                                                </CustomButton>
                                                <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                                            </FormFieldBox>
                                        </Grid>
                                    </form>
                                </FormProvider>
                            )}
                        </Box>
                    </BoxContent>
                </>
            )}
        </Box>
    );
}

export default CustomSubcategory;